/**
 * App lifecycle (foreground / background) — the ONLY module in the app that
 * imports `@capacitor/app` (phase6-contract.md §3.5).
 *
 * One subscriber today: the host-notification facade re-reads the OS
 * permission and re-checks the push registration when the native shell comes
 * back to the foreground (the counterpart of the web's `visibilitychange` +
 * `focus`, which never fire inside a WebView that the OS paused).
 *
 * Same contract as every other `services/device/` module: a no-op on the web
 * and in a shell without the plugin, and it never throws — a lifecycle hook
 * that fails to attach must not take down the boot that asked for it.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { IS_DEV } from '@/utils/env';

/** True inside a native shell that ships the App plugin. */
export function isSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('App');
}

/**
 * Listen for `appStateChange`. The handler receives `true` when the app came
 * to the foreground and `false` when it went to the background.
 *
 * @returns a detach function — always safe to call, even when nothing was
 *   attached (web, or a plugin that rejected).
 */
export async function onAppStateChange(handler: (active: boolean) => void): Promise<() => void> {
  if (!isSupported()) return () => {};
  try {
    const listener = await App.addListener('appStateChange', (state) => {
      handler(state.isActive === true);
    });
    return () => {
      void listener.remove().catch(() => undefined);
    };
  } catch (err) {
    if (IS_DEV) console.warn('[app-state] addListener', err);
    return () => {};
  }
}

/** Grouped export, mirroring the other device services. */
export const appState = {
  isSupported,
  onAppStateChange,
};
